import type { ReactNode } from 'react';
import { CalendarOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FeatureGate } from '@/shared/components/custom/feature-gate/FeatureGate';
import { isNotFoundError } from '@/shared/utils/httpErrors';
import { useCanManageOrg } from '@/features/organizations/public';
import { useMyTimeOffSummary } from '../../hooks/useMyTimeOffSummary';
import { useTimeOffActivationProbe } from '../../hooks/useTimeOffActivationProbe';

interface TimeOffActivationGateProps {
  children: ReactNode;
}

/**
 * TimeOff is plan-gated: entitlement is granted only by back-office, directly
 * against `tenant_features` — there is no self-service "Enable" flow anymore
 * (removed 2026-08-07; see the integration doc's activation rewrite). Neither
 * probe below can tell "not entitled" apart from "entitled but never
 * provisioned" — both look like the same blanket 404.
 *
 * Owner/Admin: silently calls `POST /time-off/activate` (cached — see
 * `useTimeOffActivationProbe`, so remounting this gate doesn't re-issue the
 * call within its `staleTime`). It's idempotent, so it doubles as both the
 * entitlement probe and the provisioning step — a `204` means the tenant is
 * entitled (and now provisioned); a `404` means it isn't, and there's
 * nothing an admin can do about that from here.
 *
 * Everyone else: `/activate` is Owner/Admin-only, so probe with
 * `GET /time-off/me/summary` instead — same non-actionable outcome on `404`.
 */
export function TimeOffActivationGate({ children }: TimeOffActivationGateProps) {
  const { t } = useTranslation('timeOff');
  const isAdmin = useCanManageOrg();

  const activationProbe = useTimeOffActivationProbe(isAdmin);
  const summaryQuery = useMyTimeOffSummary(!isAdmin);

  const isLoading = isAdmin ? activationProbe.isLoading : summaryQuery.isLoading;
  const isError = isAdmin ? activationProbe.isError : summaryQuery.isError;
  const error = isAdmin ? activationProbe.error : summaryQuery.error;

  const gateTestIds = {
    loading: 'time-off-activation-loading',
    unavailable: 'time-off-activation-pending-message',
    error: 'time-off-activation-error',
  };
  const gateCopy = {
    unavailable: {
      title: t('activation.pending.title'),
      description: t('activation.pending.description'),
    },
    error: {
      title: t('activation.error.title'),
      description: t('activation.error.description'),
    },
  };

  if (isLoading) {
    return <FeatureGate status="loading" icon={CalendarOff} testIds={gateTestIds} {...gateCopy} />;
  }

  if (isError) {
    return (
      <FeatureGate
        status={isNotFoundError(error) ? 'unavailable' : 'error'}
        icon={CalendarOff}
        testIds={gateTestIds}
        {...gateCopy}
      />
    );
  }

  return <>{children}</>;
}
